import * as XLSX from 'xlsx';
import { Half, halfYearMonths, toMonth } from './period';
import { sheetToAoaFullRange } from './readXlsx';
import { ChannelResult, NaverMonthRow, MethodSplit } from './types';

const METHOD_COLS = [
  { key: 'card', header: '신용카드 매출전표' },
  { key: 'cashIncome', header: '현금영수증(소득공제)' },
  { key: 'cashExpense', header: '현금영수증(지출증빙)' },
  { key: 'cashExcluded', header: '현금영수증(발행 제외)' },
  { key: 'etc', header: '기타' },
] as const;

function norm(s: any): string { return String(s ?? '').replace(/\s+/g, ''); }
function num(v: any): number { return Number(v) || 0; }
function emptySplit(): MethodSplit { return { total: 0, taxable: 0, exempt: 0 }; }
function emptyRow(month: string): NaverMonthRow {
  return {
    month, taxable: 0, exempt: 0,
    card: emptySplit(), cashIncome: emptySplit(), cashExpense: emptySplit(),
    cashExcluded: emptySplit(), etc: emptySplit(),
  };
}

export function parseNaverRows(aoa: any[][], year: number, half: Half): ChannelResult<NaverMonthRow> {
  const header = (aoa[0] || []).map(norm);
  const idx = (name: string) => header.indexOf(norm(name));
  const cDate = idx('세금신고기준일');
  const cTaxable = idx('과세매출금액');
  const cExempt = idx('면세매출금액');
  const methodIdx = METHOD_COLS.map(m => ({ key: m.key, col: idx(m.header) }));

  const target = halfYearMonths(year, half);
  const byMonth = new Map<string, NaverMonthRow>();

  for (let i = 1; i < aoa.length; i++) {
    const row = aoa[i];
    if (!row) continue;
    const month = toMonth(row[cDate]);
    if (!target.includes(month)) continue;
    const taxable = num(row[cTaxable]);
    const exempt = num(row[cExempt]);
    const rowTotal = taxable + exempt;
    if (rowTotal === 0) continue;
    const ratio = taxable / rowTotal;

    if (!byMonth.has(month)) byMonth.set(month, emptyRow(month));
    const mr = byMonth.get(month)!;
    mr.taxable += taxable;
    mr.exempt += exempt;
    for (const m of methodIdx) {
      if (m.col < 0) continue;
      const amt = num(row[m.col]);
      if (amt === 0) continue;
      const split = (mr as any)[m.key] as MethodSplit;
      split.total += amt;
      split.taxable += amt * ratio;
      split.exempt += amt * (1 - ratio);
    }
  }

  const rows = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  // 결제수단 분해 반올림
  for (const r of rows) {
    for (const m of METHOD_COLS) {
      const s = (r as any)[m.key] as MethodSplit;
      s.taxable = Math.round(s.taxable);
      s.exempt = Math.round(s.exempt);
    }
  }
  const rollup = rows.map(r => ({ month: r.month, taxable: r.taxable, exempt: r.exempt }));
  return { channel: '', rows, rollup };
}

export function parseNaverFile(
  file: File, year: number, half: Half, channel: string
): Promise<ChannelResult<NaverMonthRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = sheetToAoaFullRange(ws);
        const res = parseNaverRows(aoa, year, half);
        resolve({ ...res, channel });
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
